import { useCallback } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "@/lib/toast";
import { announceQueued, isQueued } from "@/lib/queuedStart";
import apiClient from "@/lib/api-client";
import { getAuthToken } from "@/lib/auth";
import { streamSSE } from "@/lib/sse-client";
import { useMonitorStore } from "@/stores/monitor-store";
import { trackEvent } from "@/lib/engagement";
import type {
  MonitorSettingsUpdate,
  MonitorStartResponse,
  DigestResponse,
} from "@/types/monitor";
import type { PaperWithRole } from "@/types/paper";

export function useMonitorSettings(conceptId: string) {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (settings: MonitorSettingsUpdate) => {
      await apiClient.patch(
        `api/concepts/${conceptId}/monitor/settings`,
        { json: settings },
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["concept", conceptId] });
      queryClient.invalidateQueries({ queryKey: ["concepts"] });
    },
  });

  return mutation;
}

export function useMonitorDigest(
  conceptId: string,
  options?: { sinceHours?: number; minScore?: number; limit?: number; enabled?: boolean },
) {
  const sinceHours = options?.sinceHours ?? 168;
  const minScore = options?.minScore ?? 0;
  const limit = options?.limit ?? 50;
  const enabled = options?.enabled ?? true;

  return useQuery<DigestResponse>({
    queryKey: ["monitor-digest", conceptId, sinceHours, minScore, limit],
    queryFn: async () => {
      const params = new URLSearchParams({
        since_hours: String(sinceHours),
        min_score: String(minScore),
        limit: String(limit),
      });
      return apiClient
        .get(`api/concepts/${conceptId}/monitor/digest?${params}`)
        .json<DigestResponse>();
    },
    enabled,
  });
}

export function useMonitorRun(conceptId: string) {
  const queryClient = useQueryClient();

  const startMonitor = useCallback(async () => {
    trackEvent("feature_use", { feature: "monitor", action: "started" });
    const { startMonitor, updateProgress, completeMonitor, failMonitor } =
      useMonitorStore.getState();

    try {
      const reply = await apiClient
        .post(`api/concepts/${conceptId}/monitor/start`)
        .json<MonitorStartResponse | unknown>();
      if (isQueued(reply)) {
        announceQueued(queryClient, conceptId, "The Living Lit check", reply);
        return;
      }
      const res = reply as MonitorStartResponse;

      startMonitor(res.job_id);

      // Open SSE stream
      const baseUrl = import.meta.env.VITE_API_URL ?? "http://localhost:8000";
      const token = await getAuthToken();
      const streamUrl = `${baseUrl}/api/concepts/${conceptId}/monitor/${res.job_id}/stream`;

      let receivedTerminal = false;

      for await (const event of streamSSE(streamUrl, {
        Authorization: `Bearer ${token}`,
      })) {
        if (event.event === "stage") {
          const data = JSON.parse(event.data);
          updateProgress({
            stage: data.stage,
            message: data.message,
          });
        } else if (event.event === "paper_progress") {
          const data = JSON.parse(event.data);
          const percent = Math.round((data.current / data.total) * 30); // 0-30% for collection
          updateProgress({
            currentPaper: data.current,
            totalPapers: data.total,
            message: `Collecting citations from: ${data.title}`,
            percent,
          });
        } else if (event.event === "dedup_complete") {
          const data = JSON.parse(event.data);
          updateProgress({
            message: `Deduplicated: ${data.before} → ${data.after} papers`,
            percent: 35,
          });
        } else if (event.event === "db_dedup_complete") {
          const data = JSON.parse(event.data);
          updateProgress({
            newPapersAdded: data.added,
            message: `${data.added} new papers added (${data.duplicates} duplicates)`,
            percent: 40,
          });
        } else if (event.event === "scoring_progress") {
          const data = JSON.parse(event.data);
          const scoringPercent = 40 + Math.round((data.scored / data.total) * 55); // 40-95%
          updateProgress({
            scored: data.scored,
            totalToScore: data.total,
            message: `Scoring batch ${data.batch} — ${data.scored} of ${data.total} papers`,
            percent: scoringPercent,
          });
        } else if (event.event === "auto_dismiss_complete") {
          const data = JSON.parse(event.data);
          updateProgress({
            autoDismissed: data.count,
            autoDismissThreshold: data.threshold,
          });
        } else if (event.event === "complete") {
          const data = JSON.parse(event.data);
          const { autoDismissed } = useMonitorStore.getState().progress;
          const baseMsg = data.message || `Found ${data.new_papers} new papers, scored ${data.scored}`;
          const msg = autoDismissed != null && autoDismissed > 0
            ? `${baseMsg}; auto-dismissed ${autoDismissed}`
            : baseMsg;
          completeMonitor(msg);
          toast.success("Monitor complete", { description: msg });
          receivedTerminal = true;
          await queryClient.invalidateQueries({
            queryKey: ["papers", conceptId],
          });
          await queryClient.invalidateQueries({
            queryKey: ["monitor-digest", conceptId],
          });
          await queryClient.invalidateQueries({
            queryKey: ["concept", conceptId],
          });
          return;
        } else if (event.event === "error") {
          const data = JSON.parse(event.data);
          const errMsg = data.error || "Monitor failed";
          failMonitor(errMsg);
          toast.error("Monitor failed", { description: errMsg });
          receivedTerminal = true;
          return;
        } else if (event.event === "cancelled") {
          failMonitor("Monitor cancelled");
          toast.info("Monitor cancelled");
          receivedTerminal = true;
          return;
        }
      }

      // Stream closed without terminal event — refresh as fallback
      if (!receivedTerminal) {
        completeMonitor("Monitor finished");
        toast.success("Monitor complete");
        await queryClient.invalidateQueries({
          queryKey: ["papers", conceptId],
        });
        await queryClient.invalidateQueries({
          queryKey: ["monitor-digest", conceptId],
        });
      }
    } catch (err) {
      const { failMonitor } = useMonitorStore.getState();
      const msg = err instanceof Error ? err.message : "Monitor failed";
      failMonitor(msg);
      toast.error("Monitor error", { description: msg });
    }
  }, [conceptId, queryClient]);

  const cancelMonitor = useCallback(async () => {
    const { jobId } = useMonitorStore.getState();
    if (!jobId) return;

    try {
      await apiClient.post(
        `api/concepts/${conceptId}/monitor/${jobId}/cancel`,
      );
      useMonitorStore.getState().failMonitor("Cancelled by user");
    } catch {
      // Best-effort cancel
    }
  }, [conceptId]);

  return { startMonitor, cancelMonitor };
}

export function useTriagePaper(conceptId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      paperId,
      action,
    }: {
      paperId: string;
      action: "keep" | "dismiss" | "restore";
    }) => {
      return apiClient
        .patch(`api/concepts/${conceptId}/papers/${paperId}/triage`, {
          json: { action },
        })
        .json();
    },
    onMutate: async ({ paperId, action }) => {
      await queryClient.cancelQueries({ queryKey: ["papers", conceptId] });
      const prev = queryClient.getQueriesData<{ papers: PaperWithRole[] }>({
        queryKey: ["papers", conceptId],
      });
      queryClient.setQueriesData<{ papers: PaperWithRole[] }>(
        { queryKey: ["papers", conceptId] },
        (old) => {
          if (!old) return old;
          return {
            ...old,
            papers: old.papers.map((p) =>
              p.id === paperId
                ? {
                    ...p,
                    triaged: action !== "restore",
                    exclusion_reason:
                      action === "dismiss"
                        ? "triage:dismissed"
                        : action === "restore"
                          ? null
                          : p.exclusion_reason,
                  }
                : p,
            ),
          };
        },
      );
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) {
        for (const [key, data] of ctx.prev) {
          queryClient.setQueryData(key, data);
        }
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["papers", conceptId] });
      queryClient.invalidateQueries({ queryKey: ["concepts"] });
    },
  });
}

export function useTriageBatch(conceptId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      paperIds,
      action,
    }: {
      paperIds: string[];
      action: "keep" | "dismiss";
    }) => {
      return apiClient
        .post(`api/concepts/${conceptId}/monitor/triage-batch`, {
          json: { paper_ids: paperIds, action },
        })
        .json();
    },
    onMutate: async ({ paperIds, action }) => {
      await queryClient.cancelQueries({ queryKey: ["papers", conceptId] });
      const idSet = new Set(paperIds);
      const prev = queryClient.getQueriesData<{ papers: PaperWithRole[] }>({
        queryKey: ["papers", conceptId],
      });
      queryClient.setQueriesData<{ papers: PaperWithRole[] }>(
        { queryKey: ["papers", conceptId] },
        (old) => {
          if (!old) return old;
          return {
            ...old,
            papers: old.papers.map((p) =>
              idSet.has(p.id)
                ? {
                    ...p,
                    triaged: true,
                    exclusion_reason:
                      action === "dismiss"
                        ? "triage:dismissed"
                        : p.exclusion_reason,
                  }
                : p,
            ),
          };
        },
      );
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) {
        for (const [key, data] of ctx.prev) {
          queryClient.setQueryData(key, data);
        }
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["papers", conceptId] });
      queryClient.invalidateQueries({ queryKey: ["concepts"] });
    },
  });
}
